<script setup>
import { computed, defineEmits, defineProps } from 'vue';
import {Icon} from '@iconify/vue';
import { formatNumber } from '@/helper';

const emit = defineEmits(['openHowToModal']);


const props = defineProps({
    token: {
        type: Object,
        required: true
    },
    hasBoosted: {
        type: Boolean,
        required: true
    },
    loading: {
        type: Boolean,
        required: true
    },
    hasError: {
        type: Boolean,
        required: true
    }
});
  

const openHowToModal = () => {
    emit('openHowToModal');
}

const computedWidth = () => {
  return props.token?.percentComplete !== undefined ? `${props.token.percentComplete}%` : '0%';
};

const shouldRender = computed(() => {
  return props.hasBoosted && props.token.length > 0 && props.hasError === false;
});
</script>

<template>
    <div class="flex flex-col lg:flex-row mb-4 gap-6">
        <div class="w-full">
            <h1 class="text-[3.5rem] font-bold text-black text-center lg:text-left sono">WenMoon</h1>
            <div class="text-black  mb-6 text-2xl text-center lg:text-left w-full lg:max-w-[550px] font-medium">
                <span>Solana's #1 no-code launchpad. Send memecoins for under $2 risk-free. <span @click="openHowToModal" class="text-moneyGreen underline text-2xl ml-1 cursor-pointer">How it works?</span></span>
            </div>
        </div>
        <div class="w-full flex flex-col gap-6">
            <div class="flex justify-center items-center relative">
                <div class="relative rounded-lg bg-primary w-fit pl-6 pr-2 pb-[3px] pt-[1px] font-semibold border-2 flex items-center gap-1.5">
                    <span>WenMoon: Moonshot</span>
                    <div class="has-tooltip relative">
                        <Icon icon="lucide:info" class="text-sm cursor-pointer ml-2 mt-1"/>
                        <span class='tooltip text-xs rounded-lg shadow-lg py-1 px-2 bg-primary border-2 border-gray-900 -mt-[7.5em] right-0 max-w-[350px] w-48 top-[125px] leading-5'>
                            All meme coins with the latest Bonding Curve Progress reaching 80% will be automatically displayed here. WenMoon is a decentralized platform and does not endorse any meme coin. (DYOR)!
                        </span>
                    </div>
                    <img src="@/assets/img/check.svg" class="w-9 h-9 absolute left-[-20px] top-[-5px]" />
                    
                </div>
            
            </div>
            <div v-if="shouldRender" 
            class="flex lg:max-w-[550px] mx-auto w-full">
                <router-link :to="`/token/${token.mintAddress}`" v-if="props.token" class="bg-primary border-2 border-gray-900 rounded-lg w-full">
                    <div class="flex flex-col">
                        <div class="border-b-2 border-gray-900">
                            <div class="p-4 flex justify-between">
                                <div class="text-3xl  font-bold truncate">
                                    <span v-if="token.price_change > 0">+</span>
                                    <span v-else-if="token.price_change < 0">-</span>
                                    <span class="ml-1">{{ props.token && props.token.price_change != null ? props.token.price_change.toFixed(4) : '0.0000' }}%</span>
                                </div>
                                <div class="text-3xl">
                                    <span v-if="token.price_change > 0">↑</span>
                                    <span v-else-if="token.price_change < 0">↓</span>
                                </div>
                            </div>
                        </div>
                        <div class="flex">
                            <div class="py-4 pl-4 w-[8rem]">
                                <img crossorigin="anonymous" alt="Token Image" loading="lazy" decoding="async" 
                                class="object-cover h-16 rounded"
                                :src="props.token?.tokenImage">
                            </div>
                            <div class="grid gap-2 h-full py-4 pr-4 w-full">
                                <div class="text-xs flex items-center gap-2 justify-between">
                                <div class="flex gap-2">
                                    Created by:
                                    <router-link :to="'/profile/' + props.token?.user">
                                            <span v-if="props.token?.user"
                                            class="rounded hover:underline flex max-w-[75px] text-moneyGreen underline">
                                            {{ props.token?.user.substring(0, 5) }}...{{ props.token?.user.substring(props.token?.user.length - 5) }}
                                            </span>
                                    </router-link>
                                </div>
                                    <div class="block flex gap-4 ">
                                        <a v-if='token && token?.twitter !== ""' :href="token?.twitter" target="_blank" rel="noopener noreferrer" class=" text-gray-800 hover:underline"><Icon icon="fa6-brands:square-x-twitter" class="opacity-50 hover:opacity-100"/></a>
                                        <a v-if='token && token?.telegram !== ""' :href="token?.telegram" target="_blank" rel="noopener noreferrer" class=" text-gray-800 hover:underline"><Icon icon="bxl:telegram" class="opacity-50 hover:opacity-100"/></a>
                                        <a v-if='token && token?.website !== ""' :href="token?.website" target="_blank" rel="noopener noreferrer" class=" text-gray-800 hover:underline"><Icon icon="radix-icons:link-2" class="opacity-50 hover:opacity-100"/></a>
                                    </div>
                                </div>
                                <p class="text-sm w-full break-words">
                                    <span class="font-bold  tracking-tight">{{ props.token?.tokenName }} (ticker: {{ props.token?.tokenSymbol }}): </span>
                                </p>
                                <div class="overflow-scroll text-xs h-[40px]">
                                    {{ props.token?.tokenDescription }}
                                </div>
                                
                            </div>
                        </div>
                        <div class="flex flex-col sm:flex-row gap-2 sm:gap-0 justify-between items-center px-4 pb-4">
                            <div class="text-xs gap-3 grid w-full">
                                <p>Market Cap: 
                                    <span class="text-gray-800 bg-limen px-2 py-1 rounded-full font-medium">${{ formatNumber(props.token.marketCap_usd) }}</span>
                                </p>
                                
                            </div>
                            <div class="flex w-full items-center gap-1">
                                <div aria-valuemax="100" aria-valuemin="0" role="progressbar" data-state="indeterminate" data-max="100" class="relative h-4 overflow-hidden rounded-full w-full bg-gray-200">
                                    <div
                                    data-state="indeterminate"
                                    data-max="100"
                                    class="h-full flex-1 bg-swirl transition-all"
                                    :style="{ width: computedWidth() }"
                                    ></div>
                                </div>
                                <span class="ml-1 text-sm">({{ props.token?.percentComplete }}%)</span>
                            </div>
                        </div>   
                    </div>
                </router-link>
            </div>
            <div v-else-if="loading" class="mx-auto my-auto">
                <Icon icon="svg-spinners:bars-rotate-fade" class="text-3xl"/>
            </div>
            
        </div>
    </div>

    
</template>